/* eslint-disable import/no-cycle */
import * as actionTypes from '~/store/actions/actionTypes';
import api from '~/services';
import * as lib from '~/dependencies';
import {
  log,
  mediaStreamManager,
  resolveAudioDevice,
  isSameDevice,
  buildAudioConstraints,
  isVirtualDeviceId,
  findConnectedDevice,
  toDeviceInfo,
  conferenceMixer,
} from '~/helpers';
import messagesToast from '~/validations/messagesNotifications';
import * as actions from './index';

export const phoneDeviceInitialSetup = deviceManager => ({
  type: actionTypes.PHONE_DEVICE_SETUP,
  deviceManager,
});

/**
 * @name phoneDeviceChange
 * @description Pin one audio role to a device the user explicitly picked, or unpin it so it
 * follows the OS default again. One role at a time: picking a speaker must never pin the
 * microphone.
 * @param {'microphone'|'speaker'} kind
 * @param {{deviceId: string, label: string, groupId: string} | null} device - null to unpin.
 */
export const phoneDeviceChange = (kind, device) => ({
  type: actionTypes.PHONE_DEVICE_CHANGE,
  kind,
  device,
});

// The permission unlock below is needed at most once per app session, and the grant is
// origin-scoped rather than user-scoped, so this deliberately survives a logout.
let hasUnlockedDeviceLabels = false;

/**
 * The real microphones seen on the previous resolution, so a device CONNECTED since then can be
 * told apart from the ones that were already attached. Null until the first resolution, which
 * only records - otherwise everything present at startup would look newly connected.
 */
let knownMicrophoneIds = null;

/**
 * @name resetConnectedMicrophoneTracking
 * @description Forget which microphones were attached, so the next resolution is a baseline
 * rather than a comparison. Signing out ends the session this belongs to; tests use it to keep
 * cases independent of each other.
 */
export const resetConnectedMicrophoneTracking = () => () => {
  knownMicrophoneIds = null;
};

/**
 * @name phoneDeviceSetup
 * @description Resolve which microphone and speaker are in use and store them on redux.
 * An explicit user pin wins for as long as it resolves against the attached hardware;
 * otherwise the OS default is used. See `resolveAudioDevice` for the matching and fallback
 * rules. Safe to dispatch repeatedly - it is the handler for every `devicechange` event.
 *
 * The microphone also needs pushing, which the speaker does not: a speaker change takes effect
 * immediately through `setSinkId`, but a microphone is bound to the sender track at acquisition
 * time, so a call would otherwise stay on the device it started on forever. Whenever the resolved
 * microphone actually moves - a headset connected, the pinned device back, the active one
 * unplugged - the calls in progress are moved onto it too, so the device in the picker is always
 * the device being spoken into.
 * @param {{applyToActiveCalls?: boolean}} [options] - `applyToActiveCalls: false` resolves and
 * stores without touching live calls. Used by `setAudioDevicePreference`, which moves them itself
 * straight after: an explicit pick has already written the device it chose, so the change guard
 * below usually short-circuits and would skip the move.
 */
export const phoneDeviceSetup = ({ applyToActiveCalls = true } = {}) => async (
  dispatch,
  getState
) => {
  try {
    let devices = await navigator.mediaDevices.enumerateDevices();

    // enumerateDevices() only reveals labels once microphone permission has been granted.
    // Unlock them AT MOST ONCE, and only when they are actually blank: the previous
    // implementation called getUserMedia on every invocation, so every devicechange - including
    // plugging a headset in mid-call - grabbed the microphone.
    const labelsHidden = devices
      .filter(d => d.kind === 'audioinput')
      .every(d => !d.label);

    if (labelsHidden && !hasUnlockedDeviceLabels) {
      hasUnlockedDeviceLabels = true;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true,
      });
      stream.getTracks().forEach(track => track.stop());

      devices = await navigator.mediaDevices.enumerateDevices();
    }

    const pins = getState().system?.userPreferences?.audio || {};

    // Recorded on every resolution, including the ones that adopt nothing, so the next one
    // compares against what is attached now.
    const connected = findConnectedDevice(
      devices,
      'audioinput',
      knownMicrophoneIds
    );
    knownMicrophoneIds = devices
      .filter(
        d =>
          d.kind === 'audioinput' &&
          d.deviceId &&
          !isVirtualDeviceId(d.deviceId)
      )
      .map(d => d.deviceId);
    // An explicit choice is never overridden by a device merely being plugged in.
    const connectedMicrophone = pins.microphone?.deviceId
      ? undefined
      : toDeviceInfo(connected);
    const microphone = resolveAudioDevice(
      devices,
      'audioinput',
      pins.microphone
    );
    const speaker = resolveAudioDevice(devices, 'audiooutput', pins.speaker);

    // The ring device is a pin like the others - `system.ringDevice` - and has to be resolved
    // against the live hardware for the same reason. Its fallback is the resolved CALL SPEAKER
    // rather than the OS default, because an unset ring device has always meant "ring wherever
    // calls play". resolveAudioDevice re-anchors the pin onto whatever it matched, so pin and
    // device being equal is exactly "the chosen ring device is attached".
    const ringer = resolveAudioDevice(
      devices,
      'audiooutput',
      getState().system?.ringDevice
    );
    const ringerDevice = isSameDevice(ringer.pin, ringer.device)
      ? ringer.device
      : speaker.device;

    // Bail out when nothing changed. A single hardware event fires devicechange several times -
    // one per enumerated endpoint - and every dispatch creates a new deviceManager object that
    // re-triggers the setSinkId effect and the DeviceManager microphone preview.
    const active = getState().system?.deviceManager?.audio || {};

    // Captured before the write below, because it is what decides whether the calls in progress
    // have to be moved. On Chromium the OS default moving shows up as a label change on the
    // virtual 'default' entry rather than a new id, which `isSameDevice` also catches.
    const microphoneMoved = !isSameDevice(active.microphone, microphone.device);

    // Skips the WRITE only. A microphone connected while the OS keeps its default where it is
    // changes nothing here, and returning early on that is what stopped the call being moved.
    const stateUnchanged =
      isSameDevice(active.microphone, microphone.device) &&
      isSameDevice(active.speaker, speaker.device) &&
      isSameDevice(active.ringer, ringerDevice) &&
      isSameDevice(pins.microphone, microphone.pin) &&
      isSameDevice(pins.speaker, speaker.pin) &&
      isSameDevice(getState().system?.ringDevice, ringer.pin);

    if (!stateUnchanged) {
      dispatch(
        phoneDeviceInitialSetup({
          audio: {
            microphone: microphone.device,
            speaker: speaker.device,
            ringer: ringerDevice,
          },
          // Normalized pins only: re-anchored ids and cleared virtual ids. Never a NEW pin -
          // only phoneDeviceChange / ringDeviceSelect create those, from an explicit user pick.
          preferences: { microphone: microphone.pin, speaker: speaker.pin },
          ringDevice: ringer.pin,
        })
      );
    }

    // The hardware moved under a call in progress. Nothing else will move it: the sender holds
    // the track acquired when the call started, so the new device only reaches the far end if it
    // is put there. A no-op when no call is up.
    //
    // A microphone CONNECTED since the last resolution is named explicitly rather than left to
    // the aggregate. Two reasons, and both cost calls:
    //   - the OS does not always move its default onto a headset when it connects, so asking for
    //     'default' hands back the microphone the call is already on;
    //   - the OS may move it a moment LATER than the device appears, so the resolution that runs
    //     off this event can legitimately still see the old one.
    // Naming the device sidesteps both - it cannot resolve to anything else. An explicit pin
    // always wins, so this only fills in for a user who never chose a device.
    //
    // Deliberately ONE call with one target. Detecting this somewhere else and applying it
    // separately meant two acquisitions racing for the same senders, and the aggregate one
    // landing second pulled the call straight back off the connected device.
    if (applyToActiveCalls && (connectedMicrophone || microphoneMoved)) {
      await dispatch(applyMicrophoneToActiveCalls(connectedMicrophone));
    }
  } catch (err) {
    log.error('Failed to resolve the audio devices.', err);
  }
};

export const systemSave = data => dispatch => {
  dispatch({
    type: actionTypes.SYSTEM_SAVE,
    payload: {
      machineId: data.machineId,
      OS: data.OS,
    },
  });
};

export const systemUpdateDateTime = () => dispatch => {
  dispatch({
    type: actionTypes.UPDATER_DATE_TIME,
    payload: {
      updaterDateTime: Date.now(),
    },
  });
};

export const changeUpdaterMessage = enable => dispatch => {
  dispatch({
    type: actionTypes.UPDATER_MESSAGE,
    payload: {
      showUpdaterMessage: enable,
    },
  });
};

/**
 * Change the system and phone status
 * @param {string} status - A new status could be Busy, Available or Unavailable
 */
export const statusSystem = newStatus => dispatch => {
  // const { isRegistered } = getState().phone;
  // const hasCurrentCalls = getState().callCurrent.data.length;

  // if (newStatus === 'Busy' && isRegistered && !hasCurrentCalls) {
  //   dispatch(actions.unRegisterSession());
  // }

  // if (newStatus === 'Available' && !isRegistered) {
  //   setTimeout(() => {
  //     dispatch(actions.registerSession());
  //   }, 3500);
  // }

  dispatch({
    type: actionTypes.STATUS_SYSTEM,
    payload: {
      status: newStatus,
    },
  });

  // dispatch(actions.changeStatus(newStatus));
};

export const devMode = value => async dispatch => {
  dispatch({
    type: actionTypes.DEV_MODE,
    payload: {
      dev: value,
    },
  });
  await dispatch(actions.unRegisterSession());
  dispatch(actions.startUA());
  // dispatch(actions.registerSession());
};

export const Console = value => dispatch => {
  dispatch({
    type: actionTypes.CONSOLE,
    payload: {
      console: value,
    },
  });
  localStorage.setItem('@consoleLog', value);
  // dispatch(actions.unRegisterSession());
  dispatch(actions.registerSession());
};

export const setLastHistoryCheck = () => dispatch => {
  const today = new Date();
  const dateNow = lib.dayjs(today).format('YYYY-MM-DD HH:mm:ss');

  dispatch({
    type: actionTypes.CHECKED_HISTORY,
    payload: {
      lastHistoryCheck: dateNow,
    },
  });
};

export const checkForUpdates = (userInteraction = false) => dispatch => {
  const today = new Date();
  const dateNow = lib.dayjs(today).format('YYYY-MM-DD HH:mm:ss');

  if (!userInteraction) {
    dispatch({
      type: actionTypes.CHECK_UPDATE,
      payload: {
        checkForUpdates: dateNow,
      },
    });
  } else {
    dispatch({
      type: actionTypes.USER_INTERACTION_UPDATE_DIALOG,
      payload: {
        userInteraction: dateNow,
      },
    });
  }
};

export const checkUpdater = () => dispatch => {
  api.system
    .checkUpdates()
    .then(response => {
      const resp = response.data;
      dispatch({
        type: actionTypes.NEW_VERSION_AVAILABLE,
        payload: {
          newVersionAvailable: resp,
        },
      });
    })
    .catch(err => {
      log.error('Failed to execute the checkUpdater action.', err);
    });
};

export const lastCheckContacts = (
  lastRequestedContacts,
  value = false
) => dispatch => {
  const today = new Date();
  const dateNow = lib.dayjs(today).format('YYYY-MM-DD HH:mm:ss');

  dispatch({
    type: actionTypes.CHECKED_CONTACTS,
    payload: {
      lastSeenContacts: dateNow,
      checkedContacts: value,
      lastRequestedContacts,
    },
  });
};

export const setVersion = value => dispatch => {
  dispatch({
    type: actionTypes.SET_VERSION,
    payload: {
      version: value,
    },
  });
};

export const transferPreference = value => dispatch => {
  dispatch({
    type: actionTypes.TRANSFER_PREFERENCE,
    payload: {
      transfer: value,
    },
  });
};

export const ringDeviceSelect = value => dispatch => {
  dispatch({
    type: actionTypes.RING_DEVICE,
    payload: {
      ringDevice: value,
    },
  });
};

export const changeRingTone = (value, internalRingtone) => dispatch => {
  dispatch({
    type: actionTypes.CHANGE_RINGTONE,
    payload: {
      ringTone: value,
      internalRingtone,
    },
  });
};

export const saveStatusSound = ({
  ringTone,
  outgoingRing,
  endTone,
  busyTone,
  bipTone,
}) => dispatch => {
  dispatch({
    type: actionTypes.STATUS_SOUNDS,
    payload: {
      statusSound: {
        ringTone,
        outgoingRing,
        endTone,
        busyTone,
        bipTone,
      },
    },
  });
};

export const UpdateDeviceRingTone = device => (dispatch, getState) => {
  const { system } = getState();
  const newValues = system.sounds;
  newValues.ringTone.src.setSinkId(device);
  newValues.outgoingRing.src.setSinkId(device);

  dispatch({
    type: actionTypes.UPDATE_SOUNDS_DEVICE,
    payload: {
      sounds: {
        ...newValues,
      },
    },
  });
};

export const PlayRing = (type, play) => (dispatch, getState) => {
  const { system, callCurrent } = getState();
  const { statusSound, ringTone } = system;
  const { session } = callCurrent;

  const nextStatusSound = { ...statusSound };

  // Don't play endTone if there is an active call or it is silent.
  if (statusSound) {
    if (
      (type === 'endTone' && session) ||
      (type === 'endTone' && ringTone === 'silence') ||
      (type === 'busyTone' && ringTone === 'silence')
    ) {
      nextStatusSound[type] = false;
    } else {
      nextStatusSound[type] = play;
    }
  }
  dispatch({
    type: actionTypes.STATUS_SOUNDS,
    payload: {
      statusSound: {
        ...nextStatusSound,
      },
    },
  });
};

export const remoteVideo = src => dispatch => {
  dispatch({
    type: actionTypes.SET_REMOTE_VIDEO,
    payload: {
      remoteVideo: src || {},
    },
  });
};

export const localVideo = src => dispatch => {
  dispatch({
    type: actionTypes.SET_LOCAL_VIDEO,
    payload: {
      localVideo: src || {},
    },
  });
};

export const addLocalVideo = mediaStream => dispatch => {
  dispatch({
    type: actionTypes.SET_LOCAL_VIDEO,
    payload: {
      localVideo: mediaStream,
    },
  });
};

/**
 * @name applyMicrophoneToActiveCalls
 * @description Move every call that is already in progress onto the currently resolved
 * microphone, so a device change takes effect immediately instead of only on the next call.
 *
 * Two callers: an explicit pick in the in-call dialog, and `phoneDeviceSetup` when the hardware
 * moves the resolved device on its own. The second one used to be forbidden, because the original
 * implementation (NWA-3077) hung off a redux effect whose identity changed on every
 * `deviceManager` write and so re-acquired the microphone on writes that changed nothing. What
 * makes it safe now is the caller, not the ban: `phoneDeviceSetup` dispatches this only when the
 * resolved device actually moved, and the devicechange listener it runs from is debounced, so a
 * single hardware event cannot produce a burst of acquisitions.
 *
 * The senders are shared property. A live conference mix and the announcement player both put
 * their own tracks on them, so this only touches senders that currently carry a real device
 * track and hands the microphone to the conference graph when one is up.
 */
export const applyMicrophoneToActiveCalls = device => async (
  dispatch,
  getState
) => {
  const { system, callCurrent } = getState();
  // The device arrives here as an exact constraint, same as on the call-setup path - a pinned id,
  // or the OS-default aggregate when nothing is pinned - and it is what makes the switch real. An
  // advisory id the browser does not honour comes back as the PREVIOUS device with no error, and
  // the code below then dutifully replaces a track with another track from the same microphone:
  // the picker moves, the far end hears no change, and nothing reports a problem. A failure is
  // cheap here in a way it is not during `accept()` / `invite()`: this acquisition is ours, so the
  // catch below warns and the call keeps the microphone it had.
  //
  // `device` names one explicitly and bypasses redux entirely. Only the newly-connected-headset
  // path uses it, and it has to: that device is not the resolved one yet, and going through the
  // OS-default aggregate is exactly what does not work - the OS does not always move its default
  // onto a headset when it connects, and on Electron the aggregate's own metadata does not
  // reliably follow it when it does.
  const audio = device?.deviceId
    ? { deviceId: { exact: device.deviceId } }
    : buildAudioConstraints(
        system?.userPreferences?.audio?.microphone,
        system?.deviceManager?.audio?.microphone
      );

  /**
   * With `exact` the browser can only return the device that was asked for or throw, so this
   * should be unreachable - it is here as an invariant, because the failure it catches is
   * invisible by construction and cost the same bug twice.
   *
   * Virtual ids are exempt. Asking for the 'default' aggregate hands back a track sourced from
   * whichever real device the OS currently points at, and `getSettings()` reports THAT id - so
   * comparing the two would fail on every unpinned switch and turn the invariant into noise.
   */
  const requiredDeviceId =
    audio !== true && !isVirtualDeviceId(audio.deviceId.exact)
      ? audio.deviceId.exact
      : null;

  const warnOnDeviceMismatch = track => {
    const acquired = track?.getSettings?.().deviceId;

    if (requiredDeviceId && acquired && acquired !== requiredDeviceId) {
      log.error(
        `The microphone switch asked for ${requiredDeviceId} and got ${acquired}. The call is NOT on the selected device.`
      );
    }
  };

  try {
    // While a conference is up the senders carry mixed tracks, not the microphone: replacing
    // them would tear the mix down and each participant would stop hearing the other.
    if (conferenceMixer.isActive()) {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio,
        video: false,
      });

      warnOnDeviceMismatch(stream.getAudioTracks()[0]);

      if (!conferenceMixer.replaceMicrophone(stream.getAudioTracks()[0])) {
        stream.getTracks().forEach(track => track.stop());
      }

      return;
    }

    // Every session that has not ended, NOT just the answered ones. An outgoing call acquires the
    // microphone inside `invite()`, to build the SDP offer, long before the far end picks up - so
    // filtering on `answered` left a ringing call on the old device and the callee heard it for
    // the whole conversation. Nothing else has to be excluded here: an incoming call that is still
    // ringing has no sessionDescriptionHandler yet, and the sender check below is the real filter.
    const liveSessions = (callCurrent?.data || []).filter(e => !e.finished);

    if (liveSessions.length === 0) {
      return;
    }

    await Promise.all(
      liveSessions.map(async ({ session }) => {
        const pc = session?.sessionDescriptionHandler?.peerConnection;

        if (!pc || pc.signalingState === 'closed') {
          return;
        }

        // A track sourced from a capture device reports a deviceId in getSettings(); one
        // sourced from a Web Audio destination node does not. That is how an announcement
        // playback track is recognised and left alone for its owner to restore.
        const micSenders = pc
          .getSenders()
          .filter(
            s => s.track?.kind === 'audio' && !!s.track.getSettings?.().deviceId
          );

        if (micSenders.length === 0) {
          return;
        }

        log.debug(
          'Applying the microphone change to the active calls.',
          JSON.stringify(audio)
        );
        // One acquisition per session: `enabled` lives on the track, and two calls can
        // legitimately differ - one active, one held or muted. Sharing a single track across
        // them would collapse those states into one.
        const stream = await navigator.mediaDevices.getUserMedia({
          audio,
          video: false,
        });
        const newTrack = stream.getAudioTracks()[0];
        const previousTracks = [];

        warnOnDeviceMismatch(newTrack);

        await Promise.all(
          micSenders.map(sender => {
            // Carry over mute and hold, both of which are expressed as a disabled track.
            newTrack.enabled = sender.track.enabled;
            previousTracks.push(sender.track);

            return sender.replaceTrack(newTrack);
          })
        );

        dispatch(addLocalVideo(new MediaStream([newTrack])));

        // Release the device we just moved off, or it stays open. See NWA-3053.
        previousTracks.forEach(track => track.stop());
      })
    );
  } catch (err) {
    log.error(
      'Failed to apply the microphone change to the active calls.',
      err
    );
    lib.ToastMessage('warn', messagesToast().SYSTEM.microphone.error);
  }
};

/**
 * @name setAudioDevicePreference
 * @description Record an explicit device pick and make it take effect everywhere. The single
 * entry point for the device pickers - the ordering matters and belongs here rather than in a
 * component.
 *
 * The re-resolve in the middle is what fills the active device back in after an unpin, where
 * `phoneDeviceChange` clears it and only the OS default can say what replaces it. It also
 * re-anchors a new pin onto the live enumeration, and its own change guard makes it a no-op when
 * the pick changed nothing.
 * @param {'microphone'|'speaker'} kind
 * @param {'microphone'|'speaker'|'ringDevice'} kind
 * @param {{deviceId: string, label: string, groupId: string} | null} device - null to unpin.
 */
export const setAudioDevicePreference = (kind, device) => async dispatch => {
  // The ring device pin lives on its own key rather than under userPreferences.audio, but it is
  // resolved by the same thunk and follows the same keep-the-pin rules.
  dispatch(
    kind === 'ringDevice'
      ? ringDeviceSelect(device)
      : phoneDeviceChange(kind, device)
  );

  // Resolve only. `phoneDeviceChange` above has already written the picked device, so the change
  // guard inside usually short-circuits and its own call-moving step would never run.
  await dispatch(phoneDeviceSetup({ applyToActiveCalls: false }));

  if (kind === 'microphone') {
    await dispatch(applyMicrophoneToActiveCalls());
  }
};

export const clearAllMediaStream = () => (dispatch, getState) => {
  const { system } = getState();

  try {
    mediaStreamManager.clear();
  } catch (err) {
    log.error('Failed to stop and clean up media streams.', err);
  }

  if (system?.remoteVideo?.id) {
    dispatch(remoteVideo(null));
  }
  if (system?.localVideo?.id) {
    dispatch(localVideo(null));
  }
};

export const volumeControl = value => dispatch => {
  dispatch({
    type: actionTypes.SET_VOLUME,
    payload: {
      volume: value,
    },
  });
};

export const numberToTransfer = (value = null) => dispatch => {
  dispatch({
    type: actionTypes.NUMBER_TO_TRANSFER,
    payload: {
      numberToTransfer: value,
    },
  });
};

export const setLastDialedNumber = value => dispatch => {
  dispatch({
    type: actionTypes.SET_LAST_DIALED_NUMBER,
    payload: {
      lastDialedNumber: value,
    },
  });
};

export const numberContactToTransfer = (value = null) => dispatch => {
  dispatch({
    type: actionTypes.NUMBER_CONTACT_TO_TRANSFER,
    payload: {
      numberContactToTransfer: value,
    },
  });
};

export const addCallerIdCountryCode = value => dispatch => {
  dispatch({
    type: actionTypes.CALLER_ID_COUNTRY_CODE,
    payload: {
      callerIdCountryCode: value,
    },
  });
};

export const addTemporaryCountryCode = (value, isoCode) => dispatch => {
  dispatch({
    type: actionTypes.TEMPORARY_COUNTRY_CODE,
    payload: {
      temporaryCountryCode: value,
      temporaryISOCode: isoCode,
    },
  });
};

export const resetSystem = () => dispatch => {
  dispatch({ type: actionTypes.RESET_SYSTEM });
};

export const setStartOnBoot = startOnBoot => dispatch => {
  dispatch({
    type: actionTypes.START_ON_BOOT,
    payload: {
      startOnBoot,
    },
  });
};

export const setCallWaitingSound = callWaitingSound => dispatch => {
  dispatch({
    type: actionTypes.SET_CALL_WAITING_SOUND,
    payload: {
      callWaitingSound,
    },
  });
};

export const setSmsTermAccepted = viewed => dispatch => {
  dispatch({
    type: actionTypes.SET_ACCOUNT_SMS_BILLING_VIEWED,
    smsTermAccepted: viewed,
  });
};

export const setDisableKeypadTone = enable => dispatch => {
  dispatch({
    type: actionTypes.DISABLE_KEYPAD_TONE,
    payload: {
      disableKeypadTone: enable,
    },
  });
};

export const setIsBusy = isBusy => dispatch => {
  dispatch({
    type: actionTypes.SET_SYSTEM_IS_BUSY,
    payload: { isBusy },
  });
};

/**
 * @name setVoiceSettingsOpen
 * @description Open or close the "Voice Settings" dialog (`HeadsetManager`).
 *
 * The dialog used to own its open state, so it could only be opened by its own headset button.
 * It is in redux because the microphone warning toast has to be able to open it, and a toast is
 * rendered outside the component tree by react-toastify.
 * @param {boolean} open
 */
export const setVoiceSettingsOpen = open => dispatch => {
  dispatch({
    type: actionTypes.SET_VOICE_SETTINGS_OPEN,
    payload: { open },
  });
};

export const resetStates = () => dispatch => {
  dispatch(actions.resetPhones());
  dispatch(actions.resetCalls());
  dispatch(actions.resetContacts());
  dispatch(actions.resetCallsCurrent());
  dispatch(actions.resetStatus());
  dispatch(actions.resetConnectedMicrophoneTracking());
  dispatch(actions.resetSystem());
  dispatch(actions.resetAuth());
  dispatch(actions.resetConversations());
};
